/**
 * Branch quality score shared by the dashboard overview, the branch quality table and the
 * branch map: score = 100 - (total penalty / scored images) x 10, over every capture with a
 * stored evaluation (Invalid Photo and No Pizza included).
 */

/** Penalty per occurrence of each error counted on a capture. */
export const SCORE_PENALTIES = {
  burnedCount: 10,
  undercookedCount: 5,
  /** per missing base component or ordered extra, and per ingredient that is not on the order (a swap) */
  missingIngredientCount: 5,
  /** per component found although the order removed it */
  extraIngredientCount: 5,
  missingPizzaSaverCount: 2,
  invalidPhotoCount: 10,
  noPizzaCount: 10,
};

export type ScoreCountKey = keyof typeof SCORE_PENALTIES;

const SCORE_COUNT_KEYS = Object.keys(SCORE_PENALTIES) as ScoreCountKey[];

const COMPONENTS = "$evaluation.metadata.components_evaluation";

const arraySizeExpression = (path: string) => ({
  $cond: [{ $isArray: path }, { $size: path }, 0],
});

const flagExpression = (condition: Record<string, unknown>) => ({
  $cond: [condition, 1, 0],
});

/** Per-capture error counts, one $project field per SCORE_PENALTIES key. */
const SCORE_COUNT_FIELDS: Record<ScoreCountKey, unknown> = {
  burnedCount: flagExpression({
    $eq: ["$evaluation.metadata.doneness_evaluation.burnt.status", true],
  }),
  undercookedCount: flagExpression({
    $eq: ["$evaluation.metadata.doneness_evaluation.raw.status", true],
  }),
  missingIngredientCount: {
    $add: [
      arraySizeExpression(`${COMPONENTS}.base_components.missing`),
      arraySizeExpression(`${COMPONENTS}.extras.missing`),
      arraySizeExpression(`${COMPONENTS}.base_components.unexpected`),
    ],
  },
  extraIngredientCount: arraySizeExpression(`${COMPONENTS}.removals.found`),
  missingPizzaSaverCount: flagExpression({
    $eq: ["$evaluation.metadata.pizza_saver_evaluation.status", "NOK"],
  }),
  invalidPhotoCount: flagExpression({
    $eq: ["$evaluation.metadata.classification", "Invalid Photo"],
  }),
  noPizzaCount: flagExpression({
    $eq: ["$evaluation.metadata.classification", "No Pizza"],
  }),
};

/**
 * Aggregation pipeline: `totalImages`, `totalPenalty` and the summed error counts
 * (SCORE_PENALTIES keys) per `groupBy` ("$branch_id", or null for one row in total).
 */
export const buildScorePipeline = (
  start: Date,
  end: Date,
  branchFilter: Record<string, unknown>,
  groupBy: string | null,
) => [
  {
    $match: {
      ...branchFilter,
      "evaluation.metadata": { $exists: true },
      capture_at: {
        $gte: start.toISOString(),
        $lte: end.toISOString(),
      },
    },
  },
  { $project: { branch_id: 1, ...SCORE_COUNT_FIELDS } },
  {
    $group: {
      _id: groupBy,
      totalImages: { $sum: 1 },
      totalPenalty: {
        $sum: {
          $add: SCORE_COUNT_KEYS.map((key) => ({
            $multiply: [`$${key}`, SCORE_PENALTIES[key]],
          })),
        },
      },
      ...Object.fromEntries(
        SCORE_COUNT_KEYS.map((key) => [key, { $sum: `$${key}` }]),
      ),
    },
  },
];

export const calculateScore = (totalImages: number, totalPenalty: number) =>
  totalImages > 0 ? 100 - (totalPenalty / totalImages) * 10 : null;
